import { ChangeEvent, useState } from "react";
import { IValidator, ValidationError } from "../types/validation";

export type InputHookResult = {
  input: {
    id: string;
    value: string;
    errors: ValidationError[];
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onBlur: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  };
  setErrors: (errors: ValidationError[]) => void;
  reset: () => void;
  validate: () => ValidationError[];
  setValue: (value: string) => void;
};

export const InputDefaultState = {
  input: {
    id: "",
    value: "",
    errors: [],
    onChange: () => null,
    onBlur: () => null,
  },
  setErrors: () => null,
  reset: () => null,
  validate: () => [],
  setValue: () => null,
};

const useInputState = (
  id: string,
  initialValue: string,
  validator?: IValidator<{ value: string }>,
  alwaysValidate = false,
): InputHookResult => {
  const [value, setValue] = useState<string>(initialValue);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const internalValidate = (val: string): ValidationError[] => {
    if (validator) {
      const response = validator.validate({ value: val });
      const errs = response.errors.map((e) => ({
        key: id,
        message: e.message,
      }));
      setErrors(errs);
      return errs;
    }
    return [];
  };

  const handleChange = async (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    onblur = false,
  ) => {
    setValue(event.target.value);
    if (validator && (alwaysValidate || onblur || errors.length > 0)) {
      internalValidate(event.target.value);
    }
  };

  const reset = () => setValue(initialValue);

  return {
    input: {
      id,
      value,
      errors,
      onChange: handleChange,
      onBlur: (event) => handleChange(event, true),
    },
    setErrors,
    reset,
    validate: () => internalValidate(value),
    setValue,
  };
};

export default useInputState;
